import { useEffect, useRef, useState } from "react";
import { AlertCircle, Check, ChevronDown, FileSignature, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { SignaturePad } from "./SignaturePad";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const Terms = ({ paragraphs }) => (
  <div className="mb-6 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-5">
    {paragraphs.map((p) => (
      <p key={p.slice(0, 48)} className="mb-3 text-xs leading-relaxed text-slate-600">
        {p}
      </p>
    ))}
  </div>
);

export const CreditApplicationForm = () => {
  const [state, setState] = useState(null);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [progress, setProgress] = useState({ percent: 0, filled: 0, total: 0 });
  const [openOptional, setOpenOptional] = useState({});
  const [errors, setErrors] = useState([]);
  const [restored, setRestored] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/customer/credit-application").then(({ data: d }) => {
      setState(d);
      setData(d.data || {});
      setSavedAt(d.updated_at || null);
      if (d.progress) setProgress(d.progress);
      if (d.updated_at && !d.submitted_at && Object.keys(d.data || {}).length) setRestored(true);
    });
  }, []);

  const requiredFields = () =>
    (state?.sections || []).flatMap((s) => s.fields.filter((f) => f.required));

  const missingFields = (values) =>
    requiredFields().filter((f) => {
      const v = values[f.key];
      return f.type === "checkbox" ? !v : !String(v ?? "").trim();
    });

  const trySubmit = async () => {
    const missing = missingFields(data);
    setErrors(missing.map((f) => f.key));
    if (missing.length) {
      toast.error(
        `${missing.length} required field${missing.length === 1 ? "" : "s"} still need${
          missing.length === 1 ? "s" : ""
        } an answer — they are highlighted in red`
      );
      const first = document.querySelector(`[data-testid="ca-${missing[0].key}"]`);
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      first?.focus?.();
      return;
    }
    await persist(data, true);
  };

  const saveNow = async () => {
    clearTimeout(timer.current);
    await persist(data);
    toast.success("Draft saved — sign back in any time to pick up where you left off");
  };

  const persist = async (next, submit = false) => {
    setSaving(true);
    try {
      const { data: res } = await api.put("/customer/credit-application", {
        data: next,
        submit,
        origin_url: window.location.origin,
      });
      setSavedAt(res.updated_at);
      if (res.progress) setProgress(res.progress);
      if (submit) {
        setState((s) => ({ ...s, submitted_at: res.submitted_at }));
        toast.success(
          res.copy_sent
            ? "Credit application submitted — a signed PDF copy is on its way to your inbox"
            : "Credit application submitted"
        );
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const set = (key) => (value) => {
    const next = { ...data, [key]: value };
    if (key === "billing_same_as_shipping" && value) {
      next.billing_address = next.shipping_address || "";
      next.billing_city = next.shipping_city || "";
      next.billing_state = next.shipping_state || "";
      next.billing_zip = next.shipping_zip || "";
    }
    if (
      next.billing_same_as_shipping &&
      ["shipping_address", "shipping_city", "shipping_state", "shipping_zip"].includes(key)
    ) {
      next[key.replace("shipping", "billing")] = value;
    }
    setData(next);
    setErrors((prev) => prev.filter((k) => k !== key));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => persist(next), 900);
  };

  if (!state)
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-sm text-slate-500">
        Loading your credit application…
      </div>
    );

  const renderField = (f) => {
    const id = `ca-${f.key}`;
    const value = data[f.key] ?? (f.type === "checkbox" ? false : "");
    const span = f.width === "full" || f.type === "textarea" ? "sm:col-span-3" : "";
    const invalid = errors.includes(f.key);
    const ring = invalid
      ? "border-red-500 bg-red-50/40 focus-visible:ring-red-500"
      : "border-slate-300 focus-visible:ring-blue-600";
    const flag = invalid ? (
      <span className="mt-1.5 block text-xs font-semibold text-red-600">This field is required</span>
    ) : null;

    if (f.type === "checkbox")
      return (
        <label
          key={f.key}
          className={`flex cursor-pointer items-start gap-3 rounded-md border p-4 ${span} ${
            invalid ? "border-red-500 bg-red-50/60" : "border-slate-200 bg-slate-50"
          }`}
        >
          <input
            type="checkbox"
            checked={!!value}
            data-testid={id}
            onChange={(e) => set(f.key)(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-blue-600"
          />
          <span className="text-sm text-slate-700">
            {f.label}
            {f.required && <span className="ml-1 text-red-500">*</span>}
            {flag}
          </span>
        </label>
      );

    if (f.type === "signature")
      return (
        <div key={f.key} className={span}>
          <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {f.label}
          </Label>
          <div className="mt-2">
            <SignaturePad
              value={data[f.key] || ""}
              testId={id}
              onChange={(v) => {
                const next = { ...data, [f.key]: v };
                setData(next);
                persist(next);
              }}
            />
          </div>
        </div>
      );

    if (f.type === "radio")
      return (
        <div key={f.key} className={span}>
          <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {f.label}
            {f.required && <span className="ml-1 text-red-500">*</span>}
          </Label>
          <div
            className={`mt-2 flex flex-wrap gap-2 ${
              invalid ? "rounded-md border border-red-500 bg-red-50/40 p-2" : ""
            }`}
            data-testid={id}
          >
            {f.options.map((o) => (
              <button
                key={o}
                type="button"
                data-testid={`${id}-${o.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => set(f.key)(value === o ? "" : o)}
                className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  value === o
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
                }`}
              >
                {o}
              </button>
            ))}
          </div>
          {flag}
        </div>
      );

    return (
      <div key={f.key} className={span}>
        <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          {f.label}
          {f.required && <span className="ml-1 text-red-500">*</span>}
        </Label>
        {f.type === "textarea" ? (
          <Textarea
            value={value}
            data-testid={id}
            placeholder={f.placeholder || ""}
            onChange={(e) => set(f.key)(e.target.value)}
            className={`mt-2 min-h-[86px] ${ring}`}
          />
        ) : (
          <Input
            type={f.type}
            value={value}
            disabled={
              !!data.billing_same_as_shipping &&
              f.key.startsWith("billing_") &&
              f.key !== "billing_same_as_shipping"
            }
            data-testid={id}
            placeholder={f.placeholder || ""}
            onChange={(e) =>
              set(f.key)(f.type === "tel" ? formatPhone(e.target.value) : e.target.value)
            }
            className={`mt-2 h-11 ${ring}`}
          />
        )}
        {flag}
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm" data-testid="credit-application-form">
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 pb-6">
        <div>
          <h2 className="font-display text-xl font-extrabold tracking-tight text-slate-900">
            Customer Credit Application
          </h2>
          <p className="mt-1.5 text-sm text-slate-500">
            For {state.company_name || "your supplier"} · your answers save automatically as you
            type, so you can stop and come back any time.
          </p>
        </div>
        <div className="text-xs text-slate-500" data-testid="credit-application-status">
          {saving ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…
            </span>
          ) : state.submitted_at ? (
            <span className="flex items-center gap-1.5 font-semibold text-emerald-600">
              <Check className="h-3.5 w-3.5" /> Submitted
            </span>
          ) : savedAt ? (
            <span className="flex items-center gap-1.5 font-semibold text-emerald-600">
              <Check className="h-3.5 w-3.5" /> Draft saved {String(savedAt).slice(11, 16)} UTC
            </span>
          ) : (
            "Not started"
          )}
        </div>
      </div>

      {restored && !state.submitted_at && (
        <div
          className="mt-5 rounded-lg border border-blue-200 bg-blue-50 px-5 py-4 text-sm text-blue-900"
          data-testid="draft-restored-banner"
        >
          Welcome back — we restored your saved draft from {String(savedAt).slice(0, 10)}. Nothing
          was lost; carry on where you stopped.
        </div>
      )}

      {errors.length > 0 && (
        <div
          className="mt-5 flex items-start gap-3 rounded-lg border-2 border-red-300 bg-red-50 px-5 py-4"
          data-testid="validation-summary"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
          <p className="text-sm font-medium text-red-800">
            {errors.length} required field{errors.length === 1 ? "" : "s"} still need
            {errors.length === 1 ? "s" : ""} an answer. Each one is highlighted in red below — your
            work is already saved, so nothing is lost.
          </p>
        </div>
      )}

      <div className="mt-5" data-testid="credit-application-progress">
        <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
          <span>
            {progress.percent === 100
              ? "Every field completed"
              : `${progress.filled} of ${progress.total} fields completed`}
          </span>
          <span className="text-slate-900">{progress.percent}%</span>
        </div>
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            className="h-full rounded-full bg-blue-600 transition-[width] duration-500"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
      </div>

      {state.sections.map((section) =>
        section.optional ? (
          <section key={section.title} className="mt-8">
            <button
              type="button"
              onClick={() =>
                setOpenOptional((o) => ({ ...o, [section.title]: !o[section.title] }))
              }
              data-testid={`toggle-optional-${section.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-5 py-4 text-left transition-colors duration-200 hover:bg-slate-100"
            >
              <span className="font-display text-base font-bold tracking-tight text-slate-900">
                {section.title}
                <span className="ml-2 rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Optional
                </span>
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-slate-500 transition-transform duration-200 ${
                  openOptional[section.title] ? "rotate-180" : ""
                }`}
              />
            </button>
            {openOptional[section.title] && (
              <div className="mt-4" data-testid="optional-section-body">
                {section.terms && <Terms paragraphs={state.terms[section.terms]} />}
                <div className="grid gap-5 sm:grid-cols-3">{section.fields.map(renderField)}</div>
              </div>
            )}
          </section>
        ) : (
          <section key={section.title} className="mt-8">
            <h3 className="font-display text-base font-bold tracking-tight text-slate-900">
              {section.title}
            </h3>
            <div className="mt-4">
              {section.terms && <Terms paragraphs={state.terms[section.terms]} />}
              <div className="grid gap-5 sm:grid-cols-3">{section.fields.map(renderField)}</div>
            </div>
          </section>
        )
      )}

      <div className="mt-10 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-6">
        <Button
          onClick={trySubmit}
          disabled={saving}
          data-testid="credit-application-submit"
          className="h-11 rounded-full bg-blue-600 px-7 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95"
        >
          <FileSignature className="mr-2 h-4 w-4" />
          {state.submitted_at ? "Re-submit application" : "Submit application"}
        </Button>
        <Button
          variant="outline"
          onClick={saveNow}
          disabled={saving}
          data-testid="credit-application-save"
          className="h-11 rounded-full border-slate-300 px-6 font-semibold transition-colors duration-200 hover:bg-slate-50"
        >
          <Save className="mr-2 h-4 w-4" /> Save & finish later
        </Button>
        <p className="text-sm text-slate-500">
          Your trade references are collected separately below — no need to list them here.
        </p>
      </div>
    </div>
  );
};
